import { isPureNumericPrice } from '@/utils/price';

type GiftAmount = string | number | null | undefined;

const parseFiniteGiftAmount = (value: GiftAmount) => {
  if (!isPureNumericPrice(value)) return undefined;

  const amount = Number(value);
  return Number.isFinite(amount) ? amount : undefined;
};

export function calculateEqualTierProgress(
  currentAmount: GiftAmount,
  thresholds: GiftAmount[],
): number | undefined {
  const amount = parseFiniteGiftAmount(currentAmount);
  const parsedThresholds = thresholds.map(parseFiniteGiftAmount);

  if (
    amount === undefined ||
    parsedThresholds.length === 0 ||
    parsedThresholds.some(
      (threshold, index) =>
        threshold === undefined ||
        threshold <= 0 ||
        (index > 0 && threshold <= parsedThresholds[index - 1]!),
    )
  ) {
    return undefined;
  }

  const validThresholds = parsedThresholds as number[];
  const clampedAmount = Math.max(0, amount);
  const finalThreshold = validThresholds[validThresholds.length - 1];

  if (clampedAmount >= finalThreshold) return 100;

  const tierWidth = 100 / validThresholds.length;
  const firstThreshold = validThresholds[0];

  if (clampedAmount <= firstThreshold) {
    return (clampedAmount / firstThreshold) * (tierWidth / 2);
  }

  const upperTierIndex = validThresholds.findIndex((threshold) => clampedAmount < threshold);
  const lowerThreshold = validThresholds[upperTierIndex - 1];
  const upperThreshold = validThresholds[upperTierIndex];
  const intervalProgress = (clampedAmount - lowerThreshold) / (upperThreshold - lowerThreshold);
  const lowerNodePosition = (upperTierIndex - 0.5) * tierWidth;

  return Math.min(100, Math.max(0, lowerNodePosition + intervalProgress * tierWidth));
}

/**
 * Maps the amount completed toward the next tier onto the visual interval between
 * tier nodes. The API reports both the current amount and the remaining amount,
 * so the next threshold is derived from their sum instead of inferred from a
 * cart-level amount.
 */
export function calculateTierIntervalProgress(
  currentAmount: GiftAmount,
  needAmount: GiftAmount,
  thresholds: GiftAmount[],
): number | undefined {
  const current = parseFiniteGiftAmount(currentAmount);
  const remaining = parseFiniteGiftAmount(needAmount);
  const parsedThresholds = thresholds.map(parseFiniteGiftAmount);

  if (
    current === undefined ||
    remaining === undefined ||
    remaining < 0 ||
    parsedThresholds.length === 0 ||
    parsedThresholds.some(
      (threshold, index) =>
        threshold === undefined ||
        threshold <= 0 ||
        (index > 0 && threshold <= parsedThresholds[index - 1]!),
    )
  ) {
    return undefined;
  }

  if (remaining === 0) return 100;

  const validThresholds = parsedThresholds as number[];
  const nextThreshold = current + remaining;
  const nextTierIndex = validThresholds.findIndex(
    (threshold) => Math.abs(threshold - nextThreshold) < 0.0001,
  );

  if (nextTierIndex === -1) return undefined;

  const previousThreshold = nextTierIndex === 0 ? 0 : validThresholds[nextTierIndex - 1];
  const amountRange = nextThreshold - previousThreshold;

  if (amountRange <= 0) return undefined;

  const segmentRatio = Math.min(1, Math.max(0, (current - previousThreshold) / amountRange));
  const tierWidth = 100 / validThresholds.length;
  const segmentStart = nextTierIndex === 0 ? 0 : (nextTierIndex - 0.5) * tierWidth;
  const segmentEnd = (nextTierIndex + 0.5) * tierWidth;

  return segmentStart + segmentRatio * (segmentEnd - segmentStart);
}
